import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {CommentBoxButton} from '@github-ui/comment-box/CommentBoxButton'
import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import {ScopedCommands} from '@github-ui/ui-commands'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {usePersistedDiffCommentData} from '../../hooks/use-persisted-comment-data'
import type {DiffSide, SuggestedChangesConfiguration} from '../../conversation-types'
import {validateSuggestedChange} from '../../util/validate-suggested-change'
import styles from './AddCommentEditor.module.css'
import {ConversationCommentBox} from './ConversationCommentBox'
import {
  useGetUpdatePersistedComment,
  useIsCommentBatchPending,
  usePullRequestBasePath,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useDiffLinesActions} from '../../stores/selectors'
import type {DiffLineKey} from '../../types'
import {handleLineSelectionEffects} from '../../helpers/line-selection'
import {extractSuggestionFromMarkdown} from '../../helpers/extract-suggestion-content'
import {forceAnnouncementToScreenReaders} from '@github-ui/pull-requests/utils/force-announcement-to-screen-reader'
import {getDiffLineMarkersKey} from '../../helpers/line-helpers'

export interface AddCommentEditorProps {
  batchingEnabled?: boolean
  condensed: boolean
  fileLevelComment?: boolean
  filePath: string
  focusOnMount?: boolean
  isReplying?: boolean
  lineNumber?: number
  onAddComment?: (args: {
    commentText: string
    onCompleted?: () => void
    onError: (error: Error) => void
    submitBatch?: boolean
  }) => void
  onCancelComment?: (shouldFocusCell?: boolean) => void
  onPersistedCommentExists?: () => void
  quotedText?: string
  side?: DiffSide
  startLineNumber?: number
  threadId?: string
  suggestedChangesConfig?: SuggestedChangesConfiguration
  commentLineChangeConfig?: CommentLineChangeConfig
  setStartLineNumber?: (lineNumber: number | undefined) => void
}

export interface CommentLineChangeConfig {
  diffSide: DiffSide
  endLineNumber: number
  endDiffLineKey: DiffLineKey
  isFileLevelComment: boolean
  startConversationOnNewLine: () => void
  closeLineMovingDialog: () => void
  setErrorMessage: (message: string) => void
  startDiffLineKey?: DiffLineKey
  startLineNumber?: number
}

// Define explicit action types
const SUBMISSION_ACTION = {
  COMMENT: 'comment',
  REVIEW: 'review',
} as const

type SubmissionAction = (typeof SUBMISSION_ACTION)[keyof typeof SUBMISSION_ACTION]

export function AddCommentEditor({
  batchingEnabled,
  condensed,
  fileLevelComment,
  filePath,
  focusOnMount,
  isReplying,
  lineNumber,
  onAddComment,
  onCancelComment,
  onPersistedCommentExists,
  quotedText = '',
  side,
  startLineNumber,
  threadId,
  suggestedChangesConfig,
  commentLineChangeConfig,
}: AddCommentEditorProps) {
  const [commentText, setCommentText] = useState(quotedText)
  const [submittingAction, setSubmittingAction] = useState<SubmissionAction | null>(null)
  const quotedTextRef = useRef(quotedText)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const editorRef = useRef<CommentBoxHandle>(null)
  const commentButtonRef = useRef<HTMLButtonElement>(null)
  const commentBatchPending = useIsCommentBatchPending()
  const updatePersistedComment = useGetUpdatePersistedComment()
  const previousCommentLineConfigRef = useRef<CommentLineChangeConfig | undefined>(undefined)
  const {selectMultipleLines, clearSelectedLines} = useDiffLinesActions()
  const setErrorMessageWithScreenReaderAnnouncement = useCallback(
    (message: string | undefined) => {
      forceAnnouncementToScreenReaders(message ?? '', 100)
      setErrorMessage(message)
    },
    [setErrorMessage],
  )

  const isSubmitting = submittingAction !== null
  const basePath = usePullRequestBasePath()

  const {persistCommentToStorage, removePersistedCommentFromStorage, persistCommentOnGivenLineToStorage} =
    usePersistedDiffCommentData({
      diffSide: side,
      filePath,
      line: lineNumber,
      subjectId: basePath,
      threadId,
      handlePersistedCommentExists: ({text, shouldFocus, startLine, startSide, startLineKey, endLineKey}) => {
        if (!text) return
        setCommentText(text)
        if (shouldFocus && editorRef.current) {
          editorRef.current.focus()
          editorRef.current.moveCursorToEnd()
          persistCommentToStorage({text, startLine, startSide, startLineKey, endLineKey}) // update to ensure shouldFocus is removed
          if (startLine) {
            //if we should focus and there is a start line, it means we moved a multiline comment
            const lineOrientation = side === 'LEFT' ? 'left' : 'right'
            selectMultipleLines(
              {
                endLineKey,
                endOrientation: lineOrientation,
                startLineKey,
                startOrientation: lineOrientation,
                firstLineSelectionOnDrag: {
                  lineKey: startLineKey ?? 'b:0-l:0-r:0',
                  lineNumber: lineNumber ?? 0,
                  orientation: lineOrientation,
                  shouldStartConversation: false,
                },
              },
              handleLineSelectionEffects,
            )
          }
        }

        onPersistedCommentExists?.()
      },
      fileLevelComment: !!fileLevelComment,
    })

  useEffect(() => {
    if (quotedText !== quotedTextRef.current) {
      quotedTextRef.current = quotedText

      // The effect is only triggered when quotedText changes which only happens when "quote reply" is clicked
      // We maintain a ref to the previous quotedText to avoid unnecessary state updates mitigating performance concerns
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCommentText(quotedText)
    }
  }, [quotedText])

  useEffect(() => {
    if (commentLineChangeConfig) {
      if (extractSuggestionFromMarkdown(commentText) !== null) {
        commentLineChangeConfig.setErrorMessage(
          'Changing the line number of a comment with a suggested change is not supported.',
        )
        return
      }
      commentLineChangeConfig.closeLineMovingDialog()
      persistCommentOnGivenLineToStorage({
        text: commentText,
        givenLineDiffSide: commentLineChangeConfig.diffSide,
        givenFilePath: filePath,
        givenLine: commentLineChangeConfig.endLineNumber,
        shouldBeFileLevelComment: commentLineChangeConfig.isFileLevelComment,
        startLine: commentLineChangeConfig.startLineNumber,
        startLineKey: commentLineChangeConfig.startDiffLineKey,
        endLineKey: commentLineChangeConfig.endDiffLineKey,
      })

      removePersistedCommentFromStorage()
      if (lineNumber !== commentLineChangeConfig.endLineNumber || commentLineChangeConfig.isFileLevelComment) {
        // we don't want to cancel the current comment if the end line is the same
        commentLineChangeConfig.startConversationOnNewLine()
        onCancelComment?.(false)
      } else if (
        commentLineChangeConfig.endDiffLineKey &&
        commentLineChangeConfig.startDiffLineKey &&
        previousCommentLineConfigRef.current !== commentLineChangeConfig
      ) {
        // if the line number is the same and there is a different start line number, we need to modify the selection
        // in order to prevent infinite loops we need to track the previous comment line config. I don't love this
        // solution, but given how we have to manage the line selection in order to move multi-line comments, this was
        // necessary for changing the start line only of a multi-line comment, or moving a multi-ine comment back to
        // a single line comment.
        previousCommentLineConfigRef.current = commentLineChangeConfig
        const lineOrientation = side === 'LEFT' ? 'left' : 'right'
        selectMultipleLines(
          {
            endLineKey: commentLineChangeConfig.endDiffLineKey,
            endOrientation: lineOrientation,
            startLineKey: commentLineChangeConfig.startDiffLineKey,
            startOrientation: lineOrientation,
            firstLineSelectionOnDrag: {
              lineKey: commentLineChangeConfig.startDiffLineKey ?? 'b:0-l:0-r:0',
              lineNumber: lineNumber ?? 0,
              orientation: lineOrientation,
              shouldStartConversation: false,
            },
          },
          handleLineSelectionEffects,
        )
      } else if (!commentLineChangeConfig.startDiffLineKey) {
        clearSelectedLines()
      }
    }
  }, [
    clearSelectedLines,
    commentLineChangeConfig,
    commentText,
    filePath,
    lineNumber,
    onCancelComment,
    persistCommentOnGivenLineToStorage,
    removePersistedCommentFromStorage,
    selectMultipleLines,
    side,
  ])

  useEffect(() => {
    if (focusOnMount) {
      const timeout = setTimeout(() => {
        if (!ssrSafeDocument || !ssrSafeWindow) return

        // If the comment button is not in view, scroll it into view, so the user can see the comment box
        if (commentButtonRef.current) {
          const commentButton = commentButtonRef.current
          const commentButtonRect = commentButton.getBoundingClientRect()
          const documentClientHeight = ssrSafeDocument?.documentElement.clientHeight || 0
          const commentButtonIsVisible =
            commentButtonRect.top >= 0 &&
            commentButtonRect.bottom <= (ssrSafeWindow?.innerHeight || documentClientHeight)

          if (!commentButtonIsVisible) {
            // this element will always be rendered below the fold if not visible, so we need to scroll it into view
            // we want this to sit comfortably above the fold, so we scroll it to 2/3 of the way down the viewport
            const targetTop = commentButtonRect.top
            const documentTop = ssrSafeDocument?.documentElement.getBoundingClientRect().top || 0
            const twoThirdsOfDocumentHeight = documentClientHeight * (2 / 3)
            const twoThirdsOfClientHeight = commentButton.clientHeight * (2 / 3)
            const targetScroll = targetTop - documentTop - twoThirdsOfDocumentHeight + twoThirdsOfClientHeight

            ssrSafeWindow?.scrollTo({top: targetScroll, behavior: 'smooth'})
          }
        }

        if (editorRef.current) {
          // Focus the editor after scrolling to the comment button
          editorRef.current.focus()

          // Move the cursor to the end of the comment text
          editorRef.current.moveCursorToEnd()
        }
      }, 100)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [focusOnMount])

  const handleAddComment = ({submitBatch, action}: {submitBatch: boolean; action: SubmissionAction}) => {
    let msg = 'Failed to save comment'

    if (isSubmitting) return

    if (!commentText.trim()) {
      setErrorMessageWithScreenReaderAnnouncement(`${msg}: Body can't be blank`)
      return
    }

    setSubmittingAction(action)
    setErrorMessage(undefined)
    setErrorMessageWithScreenReaderAnnouncement(undefined)

    const suggestedChangedEvaluation = validateSuggestedChange(
      commentText,
      suggestedChangesConfig?.sourceContentFromDiffLines ?? '',
    )

    if (!suggestedChangedEvaluation.isValid) {
      setErrorMessageWithScreenReaderAnnouncement(suggestedChangedEvaluation.errorMessage)
      setErrorMessage(suggestedChangedEvaluation.errorMessage)
      setSubmittingAction(null)

      return
    }
    if (lineNumber !== undefined && !isReplying) {
      const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
      if (lineKey) updatePersistedComment(filePath, lineKey, false)
    }

    onAddComment?.({
      commentText,
      onCompleted() {
        setSubmittingAction(null)
        closeCommentBox()
      },
      onError(error) {
        setSubmittingAction(null)

        //if we failed to save, we want to re-persist the comment state
        if (lineNumber !== undefined && !isReplying) {
          const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
          if (lineKey) updatePersistedComment(filePath, lineKey, true)
        }
        if (error.message) {
          msg = `${msg}: ${error.message}`
        }
        setErrorMessageWithScreenReaderAnnouncement(msg)
      },
      submitBatch,
    })
  }

  const handleAddReviewCommentAsPending = () => {
    handleAddComment({submitBatch: false, action: SUBMISSION_ACTION.REVIEW})
  }
  const handleSubmitPendingComments = () => {
    handleAddComment({submitBatch: true, action: SUBMISSION_ACTION.COMMENT})
  }

  const closeCommentBox = useCallback(() => {
    if (lineNumber !== undefined && !isReplying) {
      const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
      if (lineKey) updatePersistedComment(filePath, lineKey, false)
    }
    removePersistedCommentFromStorage()
    setCommentText('')
    onCancelComment?.()
  }, [
    filePath,
    lineNumber,
    isReplying,
    removePersistedCommentFromStorage,
    onCancelComment,
    side,
    updatePersistedComment,
  ])
  const handleCommentTextChange = (text: string) => {
    setErrorMessageWithScreenReaderAnnouncement(undefined)
    if (lineNumber !== undefined && !isReplying) {
      const lineKey = getDiffLineMarkersKey(lineNumber, side ?? 'RIGHT')
      const hasText = text.trim().length > 0
      if (lineKey) updatePersistedComment(filePath, lineKey, hasText)
    }
    persistCommentToStorage({text, startLine: startLineNumber, startSide: startLineNumber ? side : undefined})
    setCommentText(text)
  }

  return (
    <ScopedCommands
      commands={{
        'pull-requests-conversations:submit-comment-and-review': handleSubmitPendingComments,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={styles.AddCommentEditor}
        onKeyDown={event => {
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (event.key === 'Escape' && !commentText.trim()) {
            event.preventDefault()
            closeCommentBox()
          } else {
            event.stopPropagation()
          }
        }}
      >
        <ConversationCommentBox
          ref={editorRef}
          label="Add diff comment"
          value={commentText}
          onChange={handleCommentTextChange}
          onPrimaryAction={batchingEnabled ? handleAddReviewCommentAsPending : handleSubmitPendingComments}
          suggestedChangesConfig={suggestedChangesConfig}
          markdownErrorMessage={errorMessage}
          lineNumber={lineNumber}
          filePath={filePath}
          startLineNumber={startLineNumber}
          disabled={isSubmitting}
          className={clsx(
            styles.ConversationCommentBox,
            condensed || isReplying ? styles.hideMarkdownFooter : styles.showMarkdownFooter,
            isReplying && styles.isReplying,
            condensed && styles.condensed,
          )}
        >
          {onCancelComment && (
            <CommentBoxButton className="py-1 px-2" variant="default" onClick={closeCommentBox}>
              Cancel
            </CommentBoxButton>
          )}
          {!commentBatchPending && (
            <CommentBoxButton
              className="py-1 px-2"
              variant={batchingEnabled ? 'default' : 'primary'}
              onClick={handleSubmitPendingComments}
              ref={commentButtonRef}
              loading={submittingAction === SUBMISSION_ACTION.COMMENT}
              inactive={isSubmitting || commentText.trim().length === 0}
            >
              {isReplying ? 'Reply' : 'Comment'}
            </CommentBoxButton>
          )}
          {batchingEnabled && (
            <CommentBoxButton
              className="py-1 px-2"
              variant="primary"
              onClick={handleAddReviewCommentAsPending}
              loading={submittingAction === SUBMISSION_ACTION.REVIEW}
              inactive={isSubmitting || commentText.trim().length === 0}
            >
              {commentBatchPending ? 'Add review comment' : 'Start a review'}
            </CommentBoxButton>
          )}
        </ConversationCommentBox>
      </div>
    </ScopedCommands>
  )
}
